import React, { useState, useRef, useEffect } from 'react';

interface ProjectCardProps {
  title: string;
  description: string;
  tags: string[];
  liveUrl: string;
  codeUrl: string;
  image?: string;
  stats?: { label: string; value: string };
  hideCode?: boolean;
  index?: number;
}

export default function AnimatedProjectCard({
  title,
  description,
  tags,
  liveUrl,
  codeUrl,
  image,
  stats,
  hideCode,
  index = 0
}: ProjectCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Handle mouse movement for 3D tilt effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    
    // Get position of the card
    const rect = cardRef.current.getBoundingClientRect();
    
    // Calculate mouse position relative to the card center
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    
    // Update rotation and position based on mouse position
    setRotation({
      x: y / 10, // Rotate around X-axis (up/down)
      y: -x / 10 // Rotate around Y-axis (left/right)
    });
    
    setPosition({
      x: x / 20,
      y: y / 20
    });
  };

  // Reset card position and rotation
  const handleMouseLeave = () => {
    setIsHovered(false);
    setRotation({ x: 0, y: 0 });
    setPosition({ x: 0, y: 0 });
  };

  // This effect adds a new animation when the component is rendered
  useEffect(() => {
    if (cardRef.current) {
      cardRef.current.classList.add('project-card-enter');
      cardRef.current.style.animationDelay = `${index * 0.1}s`;
    }
    
    // Cleanup animation classes after animation completes
    const timeout = setTimeout(() => {
      if (cardRef.current) {
        cardRef.current.classList.remove('project-card-enter');
      }
    }, 1000);
    
    return () => clearTimeout(timeout);
  }, [index]);
  
  return (
    <div
      ref={cardRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative min-h-[24rem] bg-white/90 dark:bg-black/60 rounded-xl shadow-lg transform transition-all duration-200 ease-out hover:shadow-xl hover:scale-[1.02]"
      style={{
        transform: `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) translate3d(${position.x}px, ${position.y}px, 0)`,
        transformStyle: 'preserve-3d'
      }}
    >
      {/* Card Header with Image or Gradient */}
      <div
        className="relative h-48 overflow-hidden rounded-t-xl flex items-center justify-center transform transition-transform duration-300"
        style={{
          transform: `translateZ(${isHovered ? '20px' : '0px'})`,
          transformStyle: 'preserve-3d'
        }}
      >
        {image ? (
          <>
            <img
              src={image}
              alt={title}
              className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 ${
                isHovered ? 'scale-110' : 'scale-100'
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-purple-700 dark:from-purple-400 dark:to-purple-600" />
        )}

        <div className="relative z-10 text-center px-6 w-full flex flex-col items-center">
          <h3 className="text-white text-xl font-semibold drop-shadow-lg">{title}</h3>
          {stats && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/20">
              <span className="text-white font-bold text-sm">{stats.value}</span>
              <span className="text-white/80 text-xs">{stats.label}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Card Body */}
      <div 
        className="p-6 transform transition-transform duration-300"
        style={{ 
          transform: `translateZ(${isHovered ? '40px' : '0px'})`,
          transformStyle: 'preserve-3d'
        }}
      >
        {/* Project Description */}
        <p className="text-gray-700 dark:text-gray-300 mb-4">{description}</p>
        
        {/* Project Tags */}
        <div className="flex flex-wrap gap-2 mb-4 transform transition-transform duration-300" style={{ 
          transform: `translateZ(${isHovered ? '60px' : '0px'})`,
        }}>
          {tags.map((tag, index) => (
            <span 
              key={index} 
              className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
        
        {/* Project Links */}
        <div 
          className="flex space-x-3 transform transition-transform duration-300"
          style={{ 
            transform: `translateZ(${isHovered ? '60px' : '0px'})`,
          }}
        >
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 bg-purple-600 dark:bg-purple-500 text-white rounded hover:bg-purple-700 dark:hover:bg-purple-600 transition-colors"
          >
            View Live
          </a>
          {!hideCode && (
            <a
              href={codeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 border border-purple-600 dark:border-purple-500 text-purple-600 dark:text-purple-500 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
            >
              Source Code
            </a>
          )}
        </div>
      </div>
      
      {/* Animated glow effect on hover */}
      {isHovered && (
        <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
      )}
    </div>
  );
}